import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Activity, DoorOpen, DoorClosed, AlertTriangle, Check, Wifi, WifiOff, User, Building2, Clock, MapPin, Home, X, Search, ChevronDown, ExternalLink, Fingerprint, Loader2, Car } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { eventPersonTypeLabel } from '../../../utils/eventPersonLabel';
import { toThumbnailUrl } from '../../../utils/thumbnailUrl';
import { Event } from '../types';
import { LicensePlate } from '../../ui/LicensePlate';
import { useIsDesktop } from '../../../hooks/useMediaQuery';
import { Drawer } from '../../ui/Drawer';

// Custom scrollbar styles for Events Panel - Always visible
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 10px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: #1f2937;
    border-radius: 5px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #4b5563;
    border-radius: 5px;
    border: 2px solid #1f2937;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }

  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: auto;
    scrollbar-color: #4b5563 #1f2937;
  }

  /* New Event Highlight Animation - Clean modern design */
  @keyframes newEventPulse {
    0%, 100% {
      background-color: rgba(59, 130, 246, 0.15);
      border-left-color: rgb(96, 165, 250);
      box-shadow: 0 0 20px rgba(59, 130, 246, 0.4);
    }
    50% {
      background-color: rgba(96, 165, 250, 0.25);
      border-left-color: rgb(147, 197, 253);
      box-shadow: 0 0 30px rgba(96, 165, 250, 0.5);
    }
  }

  .event-highlight-new {
    animation: newEventPulse 2s ease-in-out infinite;
    border-left-width: 6px !important;
  }
`;

interface EventsPanelProps {
  generalEvents: Event[];
  recentEvents: Event[];
  customerEventsCollapsed: boolean;
  setCustomerEventsCollapsed: (collapsed: boolean) => void;
  generalEventsCollapsed?: boolean;
  setGeneralEventsCollapsed?: (collapsed: boolean) => void;
  isConnected?: boolean;
  onEventClick?: (event: Event) => void;
  // When true, hides "Eventos do Cliente" section (for users with single customer)
  singleCustomerMode?: boolean;
  // Called when user wants to navigate to the full events page
  onNavigate?: (path: string) => void;
  /** 'vertical' (default) = tall sidebar with stacked, full-width cards.
   *  'horizontal' = short bottom strip; each section's cards scroll horizontally instead. */
  layout?: 'vertical' | 'horizontal';
}

// Helper to get event icon (smaller for badge)
const getEventIcon = (type: string) => {
  switch (type) {
    case 'entry':
      return <DoorOpen className="h-3 w-3" />;
    case 'exit':
      return <DoorClosed className="h-3 w-3" />;
    case 'alert':
    case 'warning':
      return <AlertTriangle className="h-3 w-3" />;
    case 'porteiro_shift_started':
    case 'porteiro_shift_completed':
      return <Check className="h-3 w-3" />;
    case 'porteiro_scan':
      return <MapPin className="h-3 w-3" />;
    case 'porteiro_shift_abandoned':
      return <AlertTriangle className="h-3 w-3" />;
    case 'remote':
      return <Fingerprint className="h-3 w-3" />;
    case 'system':
    default:
      return <Check className="h-3 w-3" />;
  }
};

// Helper to get direction label and styling
const getDirectionInfo = (type: string, direction?: string) => {
  if (direction === 'passagem') {
    return { label: 'Passagem', bgClass: 'bg-amber-500/20', textClass: 'text-amber-600' };
  }
  switch (type) {
    case 'entry':
      return { label: 'Entrada', bgClass: 'bg-green-500/20', textClass: 'text-green-400' };
    case 'exit':
      return { label: 'Saída', bgClass: 'bg-blue-500/20', textClass: 'text-blue-400' };
    case 'alert':
    case 'warning':
      return { label: 'Alerta', bgClass: 'bg-yellow-500/20', textClass: 'text-yellow-400' };
    case 'porteiro_shift_started':
      return { label: 'Turno Iniciado', bgClass: 'bg-cyan-500/20', textClass: 'text-cyan-400' };
    case 'porteiro_shift_completed':
      return { label: 'Turno Concluído', bgClass: 'bg-green-500/20', textClass: 'text-green-400' };
    case 'porteiro_scan':
      return { label: 'Verificação', bgClass: 'bg-blue-500/20', textClass: 'text-blue-400' };
    case 'porteiro_shift_abandoned':
      return { label: 'Turno Abandonado', bgClass: 'bg-red-500/20', textClass: 'text-red-400' };
    case 'revoke':
      return { label: 'Revogado', bgClass: 'bg-red-500/20', textClass: 'text-red-400' };
    case 'remote':
      return { label: 'Remota', bgClass: 'bg-indigo-500/20', textClass: 'text-indigo-400' };
    case 'system':
      return { label: 'Sistema', bgClass: 'bg-gray-500/20', textClass: 'text-gray-400' };
    default:
      return { label: 'Evento', bgClass: 'bg-gray-500/20', textClass: 'text-gray-400' };
  }
};

// Helper to get event card border color
const getEventBorderColor = (type: string) => {
  switch (type) {
    case 'entry':
      return 'border-l-green-500';
    case 'exit':
      return 'border-l-blue-500';
    case 'alert':
    case 'warning':
      return 'border-l-yellow-500';
    case 'porteiro_shift_started':
      return 'border-l-cyan-500';
    case 'porteiro_shift_completed':
      return 'border-l-green-500';
    case 'porteiro_scan':
      return 'border-l-blue-500';
    case 'porteiro_shift_abandoned':
    case 'revoke':
      return 'border-l-red-500';
    case 'remote':
      return 'border-l-indigo-500';
    default:
      return 'border-l-gray-500';
  }
};

// Helper to get location info (department or unit)
const getLocationInfo = (event: Event): string | null => {
  if (event.departmentName) return event.departmentName;
  if (event.unitName) return event.unitName;
  return null;
};

// The category map lives in utils/eventPersonLabel now — the Eventos PAGE had its own copy and
// the two had already drifted on which field wins.
const formatPersonType = (personType: string | undefined): string | null =>
  eventPersonTypeLabel(personType);

// Brazilian street prefixes used to detect street segments inside a concatenated unit_name
const STREET_PREFIXES = /^(Rua|R\.|Av\.|Avenida|Av |Alameda|Al\.|Travessa|Tv\.|Estrada|Est\.|Rodovia|Rod\.|Via |Viela|Largo|Praça|Pça\.|Condomínio|Cond\.)\s/i;

// Splits a unit_name like "Q10 - Q10LT13 - Rua João, 42" into unit + street parts.
// Falls back gracefully when no street is embedded.
const parseUnitNameStreet = (unitName: string | undefined | null): { unit: string | null; street: string | null } => {
  if (!unitName) return { unit: null, street: null };
  const parts = unitName.split(' - ');
  if (parts.length >= 2) {
    const last = parts[parts.length - 1];
    if (STREET_PREFIXES.test(last) || last.includes(',')) {
      return { unit: parts.slice(0, -1).join(' - '), street: last };
    }
  }
  return { unit: unitName, street: null };
};

// Helper to extract person name from description (removes "Entrada - " or "Saída - " prefix)
const extractPersonName = (description: string): string => {
  return description
    .replace(/^Abertura Remota\s*-\s*/i, '')
    .replace(/^Entrada\s*-\s*/i, '')
    .replace(/^Saída\s*-\s*/i, '')
    .replace(/^Passagem\s*-\s*/i, '')
    .replace(/^Acesso autorizado\s*-\s*/i, '')
    .replace(/^Acesso negado\s*-\s*/i, '');
};

// Enriched event with pre-computed helper values (performance optimization)
interface EnrichedEvent extends Event {
  locationInfo: string | null;
  streetNameInfo: string | null;
  personTypeLabel: string | null;
  directionInfo: { label: string; bgClass: string; textClass: string };
  personName: string;
  borderColor: string;
}

// Memoized Event Card Component - prevents re-renders of unchanged events
const EventCard = React.memo<{ event: EnrichedEvent; onClick: (event: Event) => void; isNew?: boolean; hideCustomerName?: boolean }>(({ event, onClick, isNew = false, hideCustomerName = false }) => {
  const [imgLoaded, setImgLoaded] = React.useState(false);
  React.useEffect(() => { setImgLoaded(false); }, [event.personPhoto]);

  // System events: minimal divider row, no avatar card
  if (event.type === 'system') {
    return (
      <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-gray-800 bg-transparent">
        <div className="flex-shrink-0 h-3.5 w-3.5 rounded-full bg-gray-800 flex items-center justify-center">
          <Activity className="h-2 w-2 text-gray-500" />
        </div>
        <span className="flex-1 text-[10px] text-gray-400 truncate">{event.personName}</span>
        <span className="flex-shrink-0 text-[10px] text-gray-500 font-mono whitespace-nowrap">{event.time.split(', ')[1] ?? event.time}</span>
      </div>
    );
  }

  const isUnknownTag = /^\d{5,}$/.test((event.personName || '').trim());
  // RFID events (tag/card access) without a vehicle plate: when we have the owner's photo, show
  // it with a small tag badge overlay; otherwise fall back to a full Wifi icon block.
  const isRFIDPersonAccess = !event.vehiclePlate && event.accessType === 'rfid' && !isUnknownTag;
  // A vehicle event is the same event with a different credential, so it takes the same
  // skeleton: fixed media column on the left, padded info column beside it. It used to be a
  // second layout entirely, which is why nothing in the feed lined up across the two.
  const isVehicle = !!event.vehiclePlate;
  // …and when the plate resolved to a person, that person's face is far more recognisable than
  // a fifth identical car glyph. The plate stays in the info column: it is the credential that
  // was actually read. No photo (unregistered plate, a car held by the unit, a resident with no
  // portrait on file) keeps the glyph — an empty frame reads worse.
  const isVehicleWithOwnerPhoto = isVehicle && !!event.personPhoto;

  return (
    <div
      onClick={() => onClick(event)}
      className={cn(
        "bg-black rounded-lg border border-gray-800 border-l-4 overflow-hidden flex min-h-[65px] lg:min-h-[70px] xl:min-h-[80px] 2xl:min-h-[90px]",
        event.borderColor,
        (event.personId || event.vehiclePlate) && "cursor-pointer hover:bg-gray-800/50 hover:border-gray-600 transition-all",
        isNew && "event-highlight-new"
      )}
    >
      {/* Media column — photo, or the glyph that stands in for one. Same width for every kind
          of event, which is what puts the text column at one x down the whole feed. */}
      <div data-testid="event-media" className="w-12 lg:w-14 xl:w-16 2xl:w-20 flex-shrink-0 relative">
        {isVehicle && !isVehicleWithOwnerPhoto ? (
          <div data-testid="event-vehicle-glyph" className="absolute inset-0 w-full h-full flex items-center justify-center bg-sky-500/15">
            <Car className="h-7 w-7 text-sky-400" />
          </div>
        ) : isUnknownTag ? (
          <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-violet-500/15">
            <Wifi className="h-7 w-7 text-violet-400" />
          </div>
        ) : event.personPhoto ? (
          <>
            {!imgLoaded && (
              <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gray-800/60">
                <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
              </div>
            )}
            <img
              data-testid="event-owner-photo"
              src={toThumbnailUrl(event.personPhoto) ?? event.personPhoto}
              alt=""
              className={cn("absolute inset-0 w-full h-full object-cover transition-opacity duration-200", imgLoaded ? "opacity-100" : "opacity-0")}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgLoaded(true)}
            />
            {isVehicleWithOwnerPhoto && (
              // Same badge grammar as the tag one below: the face says WHO, the badge says by
              // WHAT they came through.
              <div
                data-testid="event-vehicle-badge"
                className="absolute bottom-0.5 right-0.5 rounded-full p-1 ring-2 ring-black flex items-center justify-center bg-sky-500"
                title="Passagem de veículo"
              >
                <Car className="h-2.5 w-2.5 text-white" />
              </div>
            )}
            {isRFIDPersonAccess && (
              <div
                className={cn(
                  "absolute bottom-0.5 right-0.5 rounded-full p-1 ring-2 ring-black flex items-center justify-center",
                  event.status === 'success' ? 'bg-green-500' : 'bg-orange-500'
                )}
                title="Acesso por tag"
              >
                <Wifi className="h-2.5 w-2.5 text-white" />
              </div>
            )}
          </>
        ) : isRFIDPersonAccess ? (
          <div className={cn(
            "absolute inset-0 w-full h-full flex items-center justify-center",
            event.status === 'success' ? 'bg-green-500/15' : 'bg-orange-500/15'
          )}>
            <Wifi className={cn("h-7 w-7", event.status === 'success' ? 'text-green-400' : 'text-orange-400')} />
          </div>
        ) : event.type === 'remote' ? (
          <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-indigo-500/15">
            <Fingerprint className="h-7 w-7 text-indigo-400" />
          </div>
        ) : (
          <div className={cn(
            "absolute inset-0 w-full h-full flex items-center justify-center",
            event.directionInfo.bgClass
          )}>
            <User className="h-8 w-8 text-gray-400" />
          </div>
        )}
      </div>

      {/* Info Section */}
      <div data-testid="event-info" className="flex-1 min-w-0 p-2">
        {/* Customer Name Header - hidden in single-customer mode */}
        {!hideCustomerName && event.customerName && event.type !== 'system' && (
          <div className="flex items-center gap-1 mb-1 pb-1 border-b border-gray-800">
            <Building2 className="h-3 w-3 text-cyan-400 flex-shrink-0" />
            <span className="text-xs font-medium text-cyan-400 truncate">{event.customerName}</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          {/* Person Name / Plate / Tag Badge — the identity line, whatever form it takes. */}
          {isVehicle ? (
            <>
              <LicensePlate plate={event.vehiclePlate!} size="sm" maxLength={8} />
              {event.personName && (
                <p className="text-[11px] font-medium text-gray-300 leading-snug truncate mt-0.5">{event.personName}</p>
              )}
            </>
          ) : isUnknownTag ? (
            <div className="flex items-center gap-1 flex-nowrap min-w-0">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-mono text-[10px] font-bold bg-gray-900 border-gray-600 text-gray-100 tracking-wider max-w-[90px] flex-shrink min-w-0">
                <span className="truncate">{event.personName}</span>
              </span>
              <span className="whitespace-nowrap flex-shrink-0 text-[9px] font-medium text-gray-500">
                Tag não cadastrada
              </span>
            </div>
          ) : event.type === 'remote' ? (
            <>
              <p className="text-[10px] font-semibold text-indigo-400 leading-tight tracking-wide uppercase">Abertura Remota</p>
              <p className="text-xs font-medium text-gray-100 leading-snug truncate">{event.personName}</p>
            </>
          ) : (
            <p className="text-xs font-medium text-gray-100 leading-snug truncate">{event.personName}</p>
          )}

          {/* Metadata: Person Type label + Unit/Dept on separate rows */}
          {!isUnknownTag && (event.personTypeLabel || event.locationInfo || event.streetNameInfo) && (
            <div className="mt-0.5 space-y-0.5">
              {event.personTypeLabel && (
                <div>
                  <span className="px-1.5 py-0.5 text-[9px] lg:text-[9px] xl:text-[10px] font-medium bg-gray-700 text-gray-300 rounded">
                    {event.personTypeLabel}
                  </span>
                </div>
              )}
              {event.locationInfo && (
                <div className="flex items-center gap-1 text-[10px] text-gray-400">
                  <Home className="h-2.5 w-2.5 flex-shrink-0" />
                  <span className="truncate">{event.locationInfo}</span>
                </div>
              )}
              {event.streetNameInfo && (
                <div className="flex items-center gap-1 text-[10px] text-gray-400">
                  <MapPin className="h-2.5 w-2.5 flex-shrink-0" />
                  <span className="truncate">{event.streetNameInfo}</span>
                </div>
              )}
            </div>
          )}

          {/* Time + Direction Badge row */}
          <div className="flex items-center gap-2 mt-1.5">
            <div className="flex items-center gap-1 text-[10px] lg:text-[10px] xl:text-xs text-gray-300">
              <Clock className="h-3 w-3 flex-shrink-0 text-gray-400" />
              <span className="whitespace-nowrap font-medium">{event.time}</span>
            </div>
            <span className={cn(
              "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] lg:text-[9px] xl:text-[10px] font-semibold",
              event.directionInfo.bgClass,
              event.directionInfo.textClass
            )}>
              {getEventIcon(event.type)}
              {event.directionInfo.label}
            </span>
          </div>

          {/* Zone Name at bottom */}
          {event.location && (
            <div className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-300">
              <MapPin className="h-3 w-3 flex-shrink-0 text-gray-400" />
              <span className="truncate">{event.location}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

EventCard.displayName = 'EventCard';

export { EventCard };

const TYPE_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'morador', label: 'Morador' },
  { value: 'visitante', label: 'Visitante' },
  { value: 'prestador', label: 'Prestador' },
  { value: 'funcionario', label: 'Funcionário' },
  { value: 'entregador', label: 'Entregador' },
  { value: 'system', label: 'Sistema' },
];

const TYPE_MAP: Record<string, string[]> = {
  morador: ['morador', 'proprietario', 'resident'],
  visitante: ['visitante', 'visitor'],
  funcionario: ['funcionario', 'staff'],
  prestador: ['prestador', 'service_provider'],
  entregador: ['entregador', 'delivery'],
  system: ['system'],
};

const EventsPanelComponent: React.FC<EventsPanelProps> = ({
  generalEvents,
  recentEvents,
  customerEventsCollapsed,
  setCustomerEventsCollapsed,
  generalEventsCollapsed = false,
  setGeneralEventsCollapsed,
  isConnected = true,
  onEventClick,
  singleCustomerMode = false,
  onNavigate,
  layout = 'vertical',
}) => {
  const isHorizontal = layout === 'horizontal';
  const isDesktop = useIsDesktop();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Sidebar filter state
  const [nameFilter, setNameFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [customerNameFilter, setCustomerNameFilter] = useState('');
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const typeDropdownRef = useRef<HTMLDivElement>(null);

  // Track new events for highlighting
  const [newEventIds, setNewEventIds] = useState<Set<string>>(new Set());
  const prevGeneralEventIdsRef = useRef<Set<string>>(new Set());
  const prevRecentEventIdsRef = useRef<Set<string>>(new Set());
  const highlightTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Hide customer name when all visible events belong to the same customer
  const hideCustomerName = useMemo(() => {
    const allEvents = [...generalEvents, ...recentEvents];
    const names = new Set(allEvents.map(e => e.customerName).filter(Boolean));
    return singleCustomerMode || names.size <= 1;
  }, [generalEvents, recentEvents, singleCustomerMode]);

  // Pre-compute enriched events with helper values (performance optimization)
  const enrichedGeneralEvents = useMemo<EnrichedEvent[]>(() => {
    return generalEvents.map(event => {
      const rawUnit = event.departmentName ? null : event.unitName;
      const parsed = event.streetName ? { unit: rawUnit, street: event.streetName } : parseUnitNameStreet(rawUnit);
      const locationInfo = event.departmentName || parsed.unit || null;
      return {
        ...event,
        locationInfo,
        streetNameInfo: parsed.street,
        // Prefer personRole (e.g. "Proprietário", "Familiar") over generic personType label
        personTypeLabel: event.personRole || formatPersonType(event.personType),
        directionInfo: getDirectionInfo(event.type, event.direction),
        personName: extractPersonName(event.description),
        borderColor: getEventBorderColor(event.type),
      };
    });
  }, [generalEvents]);

  const enrichedRecentEvents = useMemo<EnrichedEvent[]>(() => {
    return recentEvents.map(event => {
      const rawUnit = event.departmentName ? null : event.unitName;
      const parsed = event.streetName ? { unit: rawUnit, street: event.streetName } : parseUnitNameStreet(rawUnit);
      const locationInfo = event.departmentName || parsed.unit || null;
      return {
        ...event,
        locationInfo,
        streetNameInfo: parsed.street,
        personTypeLabel: event.personRole || formatPersonType(event.personType),
        directionInfo: getDirectionInfo(event.type, event.direction),
        personName: extractPersonName(event.description),
        borderColor: getEventBorderColor(event.type),
      };
    });
  }, [recentEvents]);

  // Filtered general events based on sidebar search/type filters
  const filteredGeneralEvents = useMemo<EnrichedEvent[]>(() => {
    return enrichedGeneralEvents.filter(event => {
      if (nameFilter && !event.personName.toLowerCase().includes(nameFilter.toLowerCase())) return false;
      if (typeFilter !== 'all') {
        if (typeFilter === 'system') {
          if (event.personType && event.personType !== 'system') return false;
        } else {
          const pt = (event.personType || '').toLowerCase();
          const allowed = TYPE_MAP[typeFilter] || [];
          if (!allowed.includes(pt)) return false;
        }
      }
      return true;
    });
  }, [enrichedGeneralEvents, nameFilter, typeFilter]);

  const activeFilterCount = (nameFilter !== '' ? 1 : 0) + (typeFilter !== 'all' ? 1 : 0);

  // Filtered customer events based on its own search box
  const filteredRecentEvents = useMemo<EnrichedEvent[]>(() => {
    if (!customerNameFilter) return enrichedRecentEvents;
    return enrichedRecentEvents.filter(event =>
      event.personName.toLowerCase().includes(customerNameFilter.toLowerCase())
    );
  }, [enrichedRecentEvents, customerNameFilter]);

  // Close type dropdown when clicking outside
  useEffect(() => {
    if (!typeDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target as Node)) {
        setTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [typeDropdownOpen]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      highlightTimersRef.current.forEach(timer => clearTimeout(timer));
      highlightTimersRef.current.clear();
    };
  }, []);

  // Detect new general events and highlight them for 15 seconds
  useEffect(() => {
    const currentIds = new Set(generalEvents.map(e => String(e.id)));

    // If this is the first time (ref is empty), just initialize without highlighting
    if (prevGeneralEventIdsRef.current.size === 0) {
      prevGeneralEventIdsRef.current = currentIds;
      return;
    }

    // Compare current events with previous to find new ones
    const newIds = Array.from(currentIds).filter(id => !prevGeneralEventIdsRef.current.has(id));

    if (newIds.length > 0) {
      // Add new event IDs to highlight set
      setNewEventIds(prev => {
        const updated = new Set(prev);
        newIds.forEach(id => updated.add(id));
        return updated;
      });

      // Create individual timer for each new event ID
      newIds.forEach(id => {
        // Clear any existing timer for this event
        const existingTimer = highlightTimersRef.current.get(id);
        if (existingTimer) {
          clearTimeout(existingTimer);
        }

        // Create new timer to remove highlight after exactly 15 seconds
        const timer = setTimeout(() => {
          setNewEventIds(prev => {
            const updated = new Set(prev);
            updated.delete(id);
            return updated;
          });
          highlightTimersRef.current.delete(id);
        }, 15000);

        highlightTimersRef.current.set(id, timer);
      });

      // Update ref with current IDs
      prevGeneralEventIdsRef.current = currentIds;
    }

    // Update ref even if no new events
    prevGeneralEventIdsRef.current = currentIds;
  }, [generalEvents]);

  // Detect new recent events and highlight them for 15 seconds
  useEffect(() => {
    const currentIds = new Set(recentEvents.map(e => String(e.id)));

    // If this is the first time (ref is empty), just initialize without highlighting
    if (prevRecentEventIdsRef.current.size === 0) {
      prevRecentEventIdsRef.current = currentIds;
      return;
    }

    // Compare current events with previous to find new ones
    const newIds = Array.from(currentIds).filter(id => !prevRecentEventIdsRef.current.has(id));

    if (newIds.length > 0) {
      // Add new event IDs to highlight set
      setNewEventIds(prev => {
        const updated = new Set(prev);
        newIds.forEach(id => updated.add(id));
        return updated;
      });

      // Create individual timer for each new event ID
      newIds.forEach(id => {
        // Clear any existing timer for this event
        const existingTimer = highlightTimersRef.current.get(id);
        if (existingTimer) {
          clearTimeout(existingTimer);
        }

        // Create new timer to remove highlight after exactly 15 seconds
        const timer = setTimeout(() => {
          setNewEventIds(prev => {
            const updated = new Set(prev);
            updated.delete(id);
            return updated;
          });
          highlightTimersRef.current.delete(id);
        }, 15000);

        highlightTimersRef.current.set(id, timer);
      });

      // Update ref with current IDs
      prevRecentEventIdsRef.current = currentIds;
    }

    // Update ref even if no new events
    prevRecentEventIdsRef.current = currentIds;
  }, [recentEvents]);

  // Check if screen has enough vertical space (>= 900px height) to show both sections
  const [hasEnoughHeight, setHasEnoughHeight] = useState(window.innerHeight >= 900);
  // Horizontal (bottom strip) mode always uses the "fixed header, no collapse" layout —
  // there's plenty of horizontal room for filters even when the strip itself is short.
  const effectiveHasEnoughHeight = isHorizontal ? true : hasEnoughHeight;

  // Refs to preserve scroll position more robustly
  const generalEventsScrollRef = React.useRef<HTMLDivElement>(null);
  const customerEventsScrollRef = React.useRef<HTMLDivElement>(null);
  const prevGeneralEventsLength = React.useRef(generalEvents.length);
  const prevCustomerEventsLength = React.useRef(recentEvents.length);

  React.useEffect(() => {
    const handleResize = () => {
      setHasEnoughHeight(window.innerHeight >= 900);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Preserve scroll position when events update - handle new events at top
  React.useEffect(() => {
    const container = generalEventsScrollRef.current;
    if (container && prevGeneralEventsLength.current !== generalEvents.length) {
      // Only auto-scroll to top if user is already at the top (within 50px)
      if (container.scrollTop > 50) {
        // User is scrolled down - keep them there
        const diff = generalEvents.length - prevGeneralEventsLength.current;
        if (diff > 0) {
          // New events added - likely at top, adjust scroll to compensate
          // Estimate: each event card is ~90px tall
          container.scrollTop += diff * 90;
        }
      }
      prevGeneralEventsLength.current = generalEvents.length;
    }
  }, [generalEvents]);

  React.useEffect(() => {
    const container = customerEventsScrollRef.current;
    if (container && prevCustomerEventsLength.current !== recentEvents.length) {
      if (container.scrollTop > 50) {
        const diff = recentEvents.length - prevCustomerEventsLength.current;
        if (diff > 0) {
          container.scrollTop += diff * 90;
        }
      }
      prevCustomerEventsLength.current = recentEvents.length;
    }
  }, [recentEvents]);

  const handleEventClick = React.useCallback((event: Event) => {
    if ((event.personId || event.vehiclePlate) && onEventClick) {
      onEventClick(event);
    }
  }, [onEventClick]);

  // Inline JSX (NOT a component) so React preserves DOM identity across re-renders.
  // If this were `const PanelContent = () => (...)`, React would treat it as a new
  // component type on every render and unmount/remount the entire list, causing
  // all <img> elements to reload their src URLs on every state update.
  const panelContent = (
    <>
      <style>{scrollbarStyles}</style>
      <div className={cn(
        "bg-gray-900 rounded-xl border border-gray-800 p-2 flex",
        // Bottom strip: the two sections sit side by side (split in half) instead of
        // stacked, so both are visible at once without doubling the strip's height.
        isHorizontal ? "flex-row gap-2" : "flex-col h-full min-h-0"
      )}>
      {/* General Events - Takes available space when expanded, shrinks when collapsed */}
      <div className={cn(
        "flex flex-col min-h-0 overflow-hidden",
        isHorizontal && "flex-1 min-w-0",
        effectiveHasEnoughHeight || !generalEventsCollapsed ? "flex-1" : "flex-shrink-0"
      )}>
        {effectiveHasEnoughHeight ? (
          // Fixed header when enough vertical space
          <div className="flex-shrink-0">
            <div className="w-full flex items-center justify-between mb-1 px-1.5 pt-1 pb-1.5 border-b border-gray-800">
              <div className="flex items-center gap-2">
                <h2 className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                  Eventos Gerais
                </h2>
                <span className="events-count px-1.5 py-0.5 bg-gray-700/80 text-gray-200 rounded text-[10px] font-semibold tabular-nums">
                  {activeFilterCount > 0 ? filteredGeneralEvents.length : generalEvents.length}
                </span>
                {activeFilterCount > 0 && (
                  <span className="events-count px-1.5 py-0.5 bg-gray-700 text-gray-300 rounded text-[10px] font-medium">
                    {activeFilterCount} filtro{activeFilterCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <button
                onClick={() => onNavigate?.('/eventos-gerais')}
                className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-300 transition-colors"
              >
                <ExternalLink className="h-2.5 w-2.5" />
                Ver todos
              </button>
            </div>
            {/* Filter row */}
            <div className="px-1.5 pb-1.5 flex items-center gap-1.5">
              <div className="flex-1 relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-500 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={nameFilter}
                  onChange={e => setNameFilter(e.target.value)}
                  className="w-full pl-6 pr-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-500"
                />
              </div>
              <div ref={typeDropdownRef} className="relative flex-shrink-0">
                <button
                  onClick={() => setTypeDropdownOpen(o => !o)}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-gray-300 hover:border-gray-500 transition-colors whitespace-nowrap"
                >
                  {TYPE_OPTIONS.find(o => o.value === typeFilter)?.label ?? 'Todos'}
                  <ChevronDown className="h-3 w-3 text-gray-500" />
                </button>
                {typeDropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 z-50 bg-gray-800 border border-gray-700 rounded shadow-lg min-w-[110px]">
                    {TYPE_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => { setTypeFilter(opt.value); setTypeDropdownOpen(false); }}
                        className={cn(
                          "w-full text-left px-3 py-1.5 text-xs hover:bg-gray-700 transition-colors",
                          typeFilter === opt.value ? "text-white font-medium" : "text-gray-300"
                        )}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          // Collapsible button when limited vertical space
          <button
            onClick={() => setGeneralEventsCollapsed && setGeneralEventsCollapsed(!generalEventsCollapsed)}
            className="w-full flex items-center justify-between mb-1.5 px-1.5 py-1 border-b border-gray-800 hover:border-gray-700 transition-colors flex-shrink-0"
          >
            <div className="flex items-center gap-2">
              <h2 className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Eventos Gerais</h2>
              {generalEvents.length > 0 && (
                <span className="events-count px-1.5 py-0.5 bg-gray-700/80 text-gray-200 rounded text-[10px] font-semibold tabular-nums">
                  {activeFilterCount > 0 ? filteredGeneralEvents.length : generalEvents.length}
                </span>
              )}
              {activeFilterCount > 0 && (
                <span className="events-count px-1.5 py-0.5 bg-gray-700 text-gray-300 rounded text-[10px]">{activeFilterCount}</span>
              )}
            </div>
            <svg className={cn("h-3.5 w-3.5 text-gray-500 transition-transform", !generalEventsCollapsed && "rotate-180")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
        {/* Filter row for collapsed-mode (only when expanded) */}
        {!effectiveHasEnoughHeight && !generalEventsCollapsed && (
          <div className="px-1.5 pb-1.5 flex items-center gap-1.5 flex-shrink-0">
            <div className="flex-1 relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-500 pointer-events-none" />
              <input
                type="text"
                placeholder="Buscar..."
                value={nameFilter}
                onChange={e => setNameFilter(e.target.value)}
                className="w-full pl-6 pr-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-500"
              />
            </div>
            <div ref={typeDropdownRef} className="relative flex-shrink-0">
              <button
                onClick={() => setTypeDropdownOpen(o => !o)}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-gray-300 hover:border-gray-500 transition-colors whitespace-nowrap"
              >
                {TYPE_OPTIONS.find(o => o.value === typeFilter)?.label ?? 'Todos'}
                <ChevronDown className="h-3 w-3 text-gray-500" />
              </button>
              {typeDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-gray-800 border border-gray-700 rounded shadow-lg min-w-[110px]">
                  {TYPE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => { setTypeFilter(opt.value); setTypeDropdownOpen(false); }}
                      className={cn(
                        "w-full text-left px-3 py-1.5 text-xs hover:bg-gray-700 transition-colors",
                        typeFilter === opt.value ? "text-white font-medium" : "text-gray-300"
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => onNavigate?.('/eventos-gerais')}
              className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-300 transition-colors flex-shrink-0"
            >
              <ExternalLink className="h-3 w-3" />
            </button>
          </div>
        )}
        {(effectiveHasEnoughHeight || !generalEventsCollapsed) && (
        <div
          ref={generalEventsScrollRef}
          className={isHorizontal
            ? "flex items-stretch gap-2 overflow-x-auto custom-scrollbar pb-1"
            : "flex-1 overflow-y-scroll space-y-1 custom-scrollbar"}
          style={isHorizontal ? undefined : { paddingRight: '4px' }}>
          {filteredGeneralEvents.length > 0 ? (
            filteredGeneralEvents.map((event) => (
              isHorizontal ? (
                <div key={event.id} className="w-72 flex-shrink-0">
                  <EventCard event={event} onClick={handleEventClick} isNew={newEventIds.has(String(event.id))} hideCustomerName={hideCustomerName} />
                </div>
              ) : (
                <EventCard key={event.id} event={event} onClick={handleEventClick} isNew={newEventIds.has(String(event.id))} hideCustomerName={hideCustomerName} />
              )
            ))
          ) : (
            <div className="flex items-center justify-center h-20 text-gray-500 text-sm">
              {activeFilterCount > 0 ? 'Nenhum evento encontrado' : 'Nenhum evento recente'}
            </div>
          )}
        </div>
        )}
      </div>

      {/* Customer Events — side by side with General Events in the horizontal bottom
       * strip (split in half), stacked below it in the vertical sidebar. Hidden in
       * single customer mode. */}
      {!singleCustomerMode && (
        <div className={cn(
          "flex flex-col min-h-0 overflow-hidden",
          isHorizontal
            ? "flex-1 min-w-0 border-l border-gray-800 pl-2"
            : cn(
                "border-t border-gray-800 mt-1.5 pt-1.5",
                !effectiveHasEnoughHeight && generalEventsCollapsed && !customerEventsCollapsed ? "flex-1" : "flex-shrink-0"
              )
        )}>
          {effectiveHasEnoughHeight ? (
            // Fixed header when enough vertical space
            <div className="flex-shrink-0">
              <div className="w-full flex items-center gap-2 mb-1 px-1.5 pt-1 pb-1.5 border-b border-gray-800">
                <h2 className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Eventos do Cliente</h2>
                {recentEvents.length > 0 && (
                  <span className="events-count px-1.5 py-0.5 bg-gray-700/80 text-gray-200 rounded text-[10px] font-semibold tabular-nums">
                    {customerNameFilter ? filteredRecentEvents.length : recentEvents.length}
                  </span>
                )}
              </div>
              <div className="px-1.5 pb-1.5">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-500 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={customerNameFilter}
                    onChange={e => setCustomerNameFilter(e.target.value)}
                    className="w-full pl-6 pr-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-500"
                  />
                </div>
              </div>
            </div>
          ) : (
            // Collapsible button when limited vertical space
            <button
              onClick={() => setCustomerEventsCollapsed(!customerEventsCollapsed)}
              className="w-full flex items-center justify-between mb-1.5 px-1.5 py-1 border-b border-gray-800 hover:border-gray-700 transition-colors"
            >
              <div className="flex items-center gap-2">
                <h2 className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Eventos do Cliente</h2>
                {recentEvents.length > 0 && (
                  <span className="px-1.5 py-0.5 bg-gray-700/80 text-gray-200 rounded text-[10px] font-semibold tabular-nums">
                    {customerNameFilter ? filteredRecentEvents.length : recentEvents.length}
                  </span>
                )}
              </div>
              <svg className={cn("h-3.5 w-3.5 text-gray-500 transition-transform", !customerEventsCollapsed && "rotate-180")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}
          {/* Search row for collapsed-mode (only when expanded) */}
          {!effectiveHasEnoughHeight && !customerEventsCollapsed && (
            <div className="px-1.5 pb-1.5 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-500 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={customerNameFilter}
                  onChange={e => setCustomerNameFilter(e.target.value)}
                  className="w-full pl-6 pr-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-500"
                />
              </div>
            </div>
          )}

          {(effectiveHasEnoughHeight || !customerEventsCollapsed) && (
            <div
              ref={customerEventsScrollRef}
              className={isHorizontal
                ? "flex items-stretch gap-2 overflow-x-auto custom-scrollbar pb-1"
                : cn(
                  "space-y-1.5 overflow-y-scroll custom-scrollbar",
                  !effectiveHasEnoughHeight && generalEventsCollapsed ? "flex-1" : "max-h-[280px]"
                )}
              style={isHorizontal ? undefined : { paddingRight: '4px' }}>
              {filteredRecentEvents.length > 0 ? (
                filteredRecentEvents.slice(0, 6).map((event) => (
                  isHorizontal ? (
                    <div key={event.id} className="w-72 flex-shrink-0">
                      <EventCard event={event} onClick={handleEventClick} isNew={newEventIds.has(String(event.id))} hideCustomerName={hideCustomerName} />
                    </div>
                  ) : (
                    <EventCard key={event.id} event={event} onClick={handleEventClick} isNew={newEventIds.has(String(event.id))} hideCustomerName={hideCustomerName} />
                  )
                ))
              ) : (
                <div className="text-center py-6 text-gray-500 text-sm">
                  {customerNameFilter ? 'Nenhum evento encontrado' : 'Nenhum evento para este cliente'}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      </div>
    </>
  );

  // Non-desktop (mobile/tablet/medium screens): Render floating hamburger button + drawer
  if (!isDesktop) {
    return (
      <>
        {/* Floating hamburger button */}
        <button
          onClick={() => setDrawerOpen(true)}
          className="fixed bottom-20 right-4 z-40 w-14 h-14 rounded-full
            bg-cyan-600 hover:bg-cyan-700 active:bg-cyan-800
            shadow-lg flex items-center justify-center
            transition-colors"
          aria-label="Abrir eventos"
        >
          <Activity className="h-6 w-6 text-white" />
          {generalEvents.length > 0 && (
            <span className="absolute -top-1 -right-1
              w-6 h-6 bg-red-500 rounded-full
              text-xs font-bold text-white
              flex items-center justify-center
              border-2 border-black">
              {generalEvents.length > 99 ? '99+' : generalEvents.length}
            </span>
          )}
        </button>

        {/* Drawer - responsive width for medium screens */}
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          position="right"
          widthClass="w-80 md:w-96 lg:w-[28rem]"
        >
          {/* Drawer Header */}
          <div className="flex-shrink-0 p-4 border-b border-gray-800 flex items-center justify-between bg-gray-900">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Activity className="h-5 w-5 text-cyan-400" />
              Eventos
            </h2>
            <button
              onClick={() => setDrawerOpen(false)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              aria-label="Fechar"
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          </div>

          {/* Panel content with max-height for medium screens */}
          <div className="flex-1 overflow-hidden">
            {panelContent}
          </div>
        </Drawer>
      </>
    );
  }

  // Desktop (1024px+): Render as sidebar with proper container
  return (
    <div className={isHorizontal ? "flex flex-col" : "h-full flex flex-col"}>
      {panelContent}
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export const EventsPanel = React.memo(EventsPanelComponent);
